/**
 * SavedSnapshotsList — left-rail list of saved report snapshots for a wave.
 *
 * Brand reps save a snapshot ("Pre-Reply-Storm · Apr 17, 14:32") and
 * later click it to load that frozen point-in-time data back into the
 * current report tab — useful for before/after comparisons.
 *
 * The list groups snapshots by report_id (heatmap, propagation,
 * bridge-roi, coupling-timeline) so navigating between report tabs
 * always shows the relevant prior captures.
 */
import React from 'react';
import { Clock3, Layers } from 'lucide-react';

const REPORT_LABEL = {
  memo: 'Memo',
};

function freshness(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.round(ms / 60000);
  if (m < 1)    return 'just now';
  if (m < 60)   return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24)   return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}

export default function SavedSnapshotsList({
  snapshots = [],
  activeReportId,
  onLoad,
  isLoading,
}) {
  // Pin snapshots for current report on top; show others as ghosted.
  const sorted = [...snapshots].sort((a, b) => {
    const aMatch = a.report_id === activeReportId ? 0 : 1;
    const bMatch = b.report_id === activeReportId ? 0 : 1;
    if (aMatch !== bMatch) return aMatch - bMatch;
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return (
    <aside
      data-testid="snapshots-list"
      className="rounded-xl p-4 flex flex-col gap-3"
      style={{ background: '#181A20', border: '1px solid #2E3240' }}
    >
      <div className="flex items-center gap-2">
        <Layers className="h-3.5 w-3.5" style={{ color: '#A78BFA' }} />
        <p className="text-[10.5px] uppercase tracking-[0.18em] font-black text-gray-300">
          Saved snapshots
        </p>
      </div>
      {isLoading ? (
        <p className="text-[11.5px] text-gray-500">Loading…</p>
      ) : sorted.length === 0 ? (
        <p className="text-[11.5px] text-gray-500 leading-relaxed">
          Save a snapshot to track week-over-week. Snapshots load back into the report tab they came from.
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {sorted.map(s => {
            const matches = s.report_id === activeReportId;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => matches && onLoad?.(s)}
                  disabled={!matches}
                  data-testid={`snapshot-row-${s.id}`}
                  className={`w-full text-left rounded-md px-2.5 py-2 transition-colors ${matches ? 'hover:bg-[#1a1f2b] cursor-pointer' : 'cursor-default opacity-50'}`}
                  style={{ border: '1px solid transparent' }}
                  title={matches ? 'Click to load this snapshot into the current tab' : `Switch to the ${REPORT_LABEL[s.report_id]} tab to load this snapshot`}
                >
                  <p className="text-[11.5px] font-bold text-white truncate" data-testid={`snapshot-row-${s.id}-label`}>
                    {s.label}
                  </p>
                  <p className="text-[9.5px] text-gray-500 uppercase tracking-[0.10em] font-bold mt-1 flex items-center gap-1.5">
                    <Clock3 className="h-2.5 w-2.5" />
                    {freshness(s.created_at)}
                    <span className="text-gray-600">·</span>
                    {REPORT_LABEL[s.report_id] || s.report_id}
                  </p>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}
